import {
  Button,
  Flex,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Modal } from '@lon/mathnation-suit/components';
import { useQueryParams } from '@lon/mathnation-suit/hooks';
import { useCreatePasswordRecoveryMutation } from '@lon/mathnation-suit/requests';
import { FormValues, getValidation, useCheckToken } from './duck';
import { PasswordRequirements, Form as ResetForm } from './components';

const PasswordReset: React.FC = () => {
  const navigate = useNavigate();

  const [params] = useQueryParams();
  useEffect(() => {
    if (!params.token) {
      navigate('/mathnation/login');
    }
  }, [params]);

  const { loading, isExpired, errored } = useCheckToken();

  const toast = useToast();
  const [recoverPassword] = useCreatePasswordRecoveryMutation();
  return (
    <Modal isOpen onClose={() => {}} size="5xl" hasCloseButton={false}>
      <Text as="h2" fontSize="lg" fontWeight="bold" mb={6}>
        Reset Password
      </Text>

      {loading && (
        <Flex
          justifyContent="center"
          mt={6}
          mb={6}
          minH="250px"
          alignItems="center"
        >
          <Spinner color="blue.500" />
        </Flex>
      )}

      {(isExpired || errored) && (
        <VStack spacing={4}>
          <Text fontWeight="bold" color="red.500">
            {!isExpired && errored === 'general'
              ? 'Something went wrong.'
              : 'Your Password Reset Link has expired.'}
          </Text>
          <Text>Please request a new one to reset your Password.</Text>
          <Button
            colorScheme="blue"
            onClick={() => navigate('/mathnation/login')}
          >
            Back To Login
          </Button>
        </VStack>
      )}

      {!loading && !isExpired && !errored && (
        <Form<FormValues>
          {...getValidation()}
          mode="onSubmit"
          onSubmit={({ newPassword }, setError) =>
            recoverPassword({
              variables: {
                input: {
                  token: params.token,
                  newPassword: newPassword,
                },
              },
            })
              .then((response) => {
                if (response?.data?.createPasswordRecovery) {
                  toast({
                    title: 'Password has been reset successfully',
                    status: 'success',
                    isClosable: true,
                  });
                  if (params['district-id'] && params.state) {
                    navigate('/mathnation/login', {
                      state: {
                        districtId: params['district-id'],
                        stateValue: params.state,
                      },
                    });
                  }
                } else {
                  toast({
                    title: 'New Password could not be saved.',
                    status: 'error',
                    isClosable: true,
                  });
                }
              })
              .catch((error) => {
                const code = error?.graphQLErrors?.[0]?.message;
                if (code === 'VALIDATION_CONSTRAINT_PASSWORD_PREVIOUS') {
                  setError('newPassword', {
                    message:
                      'Your password must not be the same as the last five used before. Please try another option.',
                  });
                } else {
                  toast({
                    title: 'New Password could not be saved.',
                    status: 'error',
                    isClosable: true,
                  });
                }
              })
          }
        >
          <Flex gridGap={4}>
            <PasswordRequirements />
            <ResetForm />
          </Flex>
        </Form>
      )}
    </Modal>
  );
};

export default PasswordReset;
